import { useState, useEffect, useContext, useCallback } from 'react';
import { List } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './ChatList.scss';
import './NotificationPage.scss';
import { getChatList, markMessageAsRead } from '../../services/chatService';
import { getNotificationGroupByType, markNotificationAsRead } from '@services/notificationService';
import { formatDate } from '@utils/dateHelper';
import NoStylist from '@components/NoStylist';
import { CountContext } from '../../context/CountContext';
import NotificationItem from './NotificationItem';
import { getAllNotificationCount } from '@services/chatService';
import { useTranslation } from 'react-i18next';
import type { ChatListItem, NotificationGroup } from '@app-types/models/chat';

interface ChatData {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  avatar: string;
}

interface ChatItemProps {
  chat: ChatData;
  onClick: () => void;
}

const ChatItem: React.FC<ChatItemProps> = ({ chat, onClick }) => {
  return (
    <div className='chat-item' onClick={onClick}>
      <div className='avatar'>
        <img src={chat.avatar} alt={chat.name} />
      </div>
      <div className='chat-content'>
        <div className='chat-header'>
          <h4>{chat.name}</h4>
          <span className='timestamp'>{chat.timestamp}</span>
        </div>
        <div className='chat-footer'>
          <p className='last-message'>{chat.lastMessage}</p>
          {chat.unreadCount > 0 && <span className='unread-count'>{chat.unreadCount}</span>}
        </div>
      </div>
    </div>
  )
}

const ChatList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const countContext = useContext(CountContext);
  const updateCount = countContext?.updateCount;

  const [chatList, setChatList] = useState<ChatListItem[]>([]);
  const [notifications, setNotifications] = useState<NotificationGroup[]>([]);
  const [noStylist, setNoStylist] = useState(false);

  // Memoize the updateCount function to prevent unnecessary re-renders
  const stableUpdateCount = useCallback(
    (key: string, value: number) => { updateCount?.(key, value); },
    [updateCount]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const list = await getChatList();
        setChatList(Array.isArray(list) ? list : []);
        const notificationList = await getNotificationGroupByType();
        setNotifications(Array.isArray(notificationList) ? notificationList : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        if ((error as { response?: { data: { code: string } } }).response && (error as { response: { data: { code: string } } }).response.data.code === "STAFF_NOT_FOUND") {
          setNoStylist(true);
        }
      }
    };

    fetchData();
  }, [stableUpdateCount]);

  const handleCustomerClick = async (userId: string) => {
    console.log(`User ID to navigate: ${userId}`);
    await markMessageAsRead(userId);
    const count = await getAllNotificationCount();
    stableUpdateCount('totalCount', count.data.totalUnread);
    navigate(`/chat/${userId}`);
  };

  const handleNotificationClick = async (type: string) => {
    console.log(`Notification type to navigate: ${type}`);
    await markNotificationAsRead(type);
    const count = await getAllNotificationCount();
    stableUpdateCount('totalCount', count.data.totalUnread);
    navigate(`/notification/${type}`);
  };

  if (noStylist) {
    return <NoStylist noStylistMessage={t('common.chatListNoStylistMessage')} />;
  }

  return (
    <div className="customer-list-container">
      <List sx={{ p: 0 }} className='notification-list-container'>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification._id}
            notification={{
              id: notification._id,
              type: notification._id,
              content: notification.lastMessage.content,
              timestamp: formatDate(notification.lastMessage.createdAt),
              isNew: !notification.lastMessage.read,
              subject: notification.lastMessage.subject,
              link: notification.lastMessage.link,
            }}
            onClick={() => handleNotificationClick(notification._id)}
          />
        ))}
      </List>
      <List sx={{ p: 0 }}>
        {chatList.map((chat) => (
          <ChatItem
            key={chat._id}
            chat={{
              id: chat._id,
              name: chat.chatUserName,
              lastMessage: chat.lastMessage.content || 'Sent an image',
              timestamp: formatDate(chat.lastMessage.createdAt),
              unreadCount: chat.count,
              avatar:
                chat.avatar ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(chat.chatUserName)}&background=random`,
            }}
            onClick={() => handleCustomerClick(chat._id)}
          />
        ))}
      </List>
    </div>
  );
};

export default ChatList;
